import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, ChevronDown, RefreshCw } from 'lucide-react';
import { useNews } from '../../hooks/useNews';
import { NewsWithViewStatus } from '../../types/news';
import { NewsItem } from './NewsItem';
import { NewsDetailsModal } from './NewsDetailsModal';
import { Badge } from '../Badge/Badge';
import { ButtonV2 } from '../ButtonV2/ButtonV2';
import { Skeleton } from '../Skeleton/Skeleton';
import CONFIG from '../../config/config';

export const NewsFlyout: React.FC = () => {
  const { t } = useTranslation();
  const {
    news,
    allNews,
    totalCount,
    isLoading,
    error,
    hasMore,
    unreadCount,
    loadMoreNews,
    markAsViewedById,
    refreshNews
  } = useNews();
  


  const [selectedNews, setSelectedNews] = useState<NewsWithViewStatus | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleViewDetails = (news: NewsWithViewStatus) => {
    setSelectedNews(news);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedNews(null);
  };

  const handleMarkAsViewed = (newsId: number) => {
    markAsViewedById(newsId);
  };

  const toggleDropdown = () => {
    const isMobile = window.innerWidth < CONFIG.MOBILE_BREAKPOINT;
    const newState = !isDropdownOpen;
    setIsDropdownOpen(newState);
    
    if (newState && isMobile) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else if (!newState && isMobile) {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isDropdownOpen) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDropdownOpen]);

  useEffect(() => {
    return () => {
      if (window.innerWidth < CONFIG.MOBILE_BREAKPOINT) {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
      }
    };
  }, []);



  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <button 
          onClick={toggleDropdown}
          className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <Bell className="h-5 w-5 text-gray-600" />
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center text-xs p-0"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </button>

        {isDropdownOpen && (
          <>
            <div className="fixed inset-0 bg-black bg-opacity-50 md:hidden" onClick={() => setIsDropdownOpen(false)} />
            
            <nav className="fixed inset-0 flex flex-col bg-white z-50 md:hidden">
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('news.title') || 'News'}
                </h3>
                <button
                  onClick={() => setIsDropdownOpen(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none text-gray-700"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4">
                <div className="flex items-center justify-end mb-4">
                  <ButtonV2
                    variant="ghost"
                    size="icon"
                    onClick={refreshNews}
                    disabled={isLoading}
                    className="p-1 h-8 w-8"
                  >
                    <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                  </ButtonV2>
                </div>

                {isLoading && news.length === 0 ? (
                  <div className="space-y-3">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="space-y-2">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-3 w-full" />
                        <Skeleton className="h-3 w-2/3" />
                      </div>
                    ))}
                  </div>
                ) : news.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Bell className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>{error || (t('news.no_news') || 'No news at the moment')}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {news.map((item) => (
                      <NewsItem
                        key={item.id}
                        news={item}
                        onMarkAsViewed={handleMarkAsViewed}
                        onViewDetails={handleViewDetails}
                      />
                    ))}

                    {hasMore && (
                      <div className="pt-2">
                        <ButtonV2
                          variant="outline"
                          onClick={loadMoreNews}
                          disabled={isLoading}
                          className="w-full"
                        >
                          {isLoading ? (
                            <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                          ) : (
                            <ChevronDown className="h-4 w-4 mr-2" />
                          )}
                          {t('news.load_more') || 'Load More News'}
                        </ButtonV2>
                      </div>
                    )}

                    {allNews.length > news.length && (
                      <div className="text-xs text-gray-500 text-center pt-2 border-t">
                        {t('news.showing_recent') || 'Showing recent news'}. {t('news.total_available') || 'Total available'}: {totalCount}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </nav>
            
            <div className="absolute right-0 top-full mt-2 z-50 w-80 sm:w-96 max-w-[90vw] bg-white rounded-lg shadow-lg border border-gray-200 max-h-[80vh] overflow-hidden hidden md:block">
              <div className="p-4 max-h-[80vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t('news.title') || 'News'}
                  </h3>
                  <div className="flex items-center space-x-2">
                    <ButtonV2
                      variant="ghost"
                      size="icon"
                      onClick={refreshNews}
                      disabled={isLoading}
                      className="p-1 h-8 w-8"
                    >
                      <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                    </ButtonV2>
                  </div>
                </div>

                {isLoading && news.length === 0 ? (
                  <div className="space-y-3">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="space-y-2">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-3 w-full" />
                        <Skeleton className="h-3 w-2/3" />
                      </div>
                    ))}
                  </div>
                ) : news.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Bell className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>{error || (t('news.no_news') || 'No news at the moment')}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {news.map((item) => (
                      <NewsItem
                        key={item.id}
                        news={item}
                        onMarkAsViewed={handleMarkAsViewed}
                        onViewDetails={handleViewDetails}
                      />
                    ))}

                    {hasMore && (
                      <div className="pt-2">
                        <ButtonV2
                          variant="outline"
                          onClick={loadMoreNews}
                          disabled={isLoading}
                          className="w-full"
                        >
                          {isLoading ? (
                            <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                          ) : (
                            <ChevronDown className="h-4 w-4 mr-2" />
                          )}
                          {t('news.load_more') || 'Load More News'}
                        </ButtonV2>
                      </div>
                    )}

                    {allNews.length > news.length && (
                      <div className="text-xs text-gray-500 text-center pt-2 border-t">
                        {t('news.showing_recent') || 'Showing recent news'}. {t('news.total_available') || 'Total available'}: {totalCount}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <NewsDetailsModal
        news={selectedNews}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </>
  );
};
